import { Link } from "react-router-dom";
import Footer from "../../Components/Footer/Footer";

const About = () => {
  return (
    <div className="bg-[#0a0a0f] min-h-screen">
      {/* Header */}
      <div className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">
            About Exo Entertainment
          </h1>
          <div className="w-24 h-0.5 bg-gradient-to-r from-purple-700 to-gold mx-auto mb-6" />
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            We're not another booking agency. We're building the future of private entertainment.
          </p>
        </div>
      </div>

      {/* Headline Quote */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <blockquote className="p-8 sm:p-12 rounded-2xl bg-gradient-to-br from-purple-950/30 to-gray-900/40 border border-purple-900/30">
            <p className="text-xl sm:text-2xl text-gray-200 leading-relaxed italic font-heading">
              "Our performers aren't just dancers. They're trained in persona architecture, 
              audience psychology, and the art of private performance."
            </p>
            <div className="w-16 h-0.5 bg-gold mt-6" />
          </blockquote>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8">
            What Makes Exo Different
          </h2>

          <div className="space-y-8">
            {/* Persona-Driven */}
            <div className="p-8 rounded-2xl bg-gray-900/40 border border-gray-800/30">
              <div className="flex items-start gap-4">
                <span className="text-3xl">🎭</span>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Persona-Driven Entertainment</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Every performer develops a distinct persona — a character with depth, narrative, and energy that 
                    goes beyond choreography. When you book an Exo entertainer, you're not getting a random 
                    performance. You're getting a curated experience crafted by someone who understands how to 
                    command a room, read an audience, and create moments that stay with you.
                  </p>
                </div>
              </div>
            </div>

            {/* Trained Performers */}
            <div className="p-8 rounded-2xl bg-gray-900/40 border border-gray-800/30">
              <div className="flex items-start gap-4">
                <span className="text-3xl">👑</span>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Trained Performers</h3>
                  <p className="text-gray-400 leading-relaxed">
                    Our entertainers are trained in persona development, audience psychology, and performance art. 
                    They understand pacing, energy management, and how to tailor a show to the room. This isn't 
                    an app where anyone signs up — it's a curated roster of professionals who take their craft seriously.
                  </p>
                </div>
              </div>
            </div>

            {/* Curated Experiences */}
            <div className="p-8 rounded-2xl bg-gray-900/40 border border-gray-800/30">
              <div className="flex items-start gap-4">
                <span className="text-3xl">✨</span>
                <div>
                  <h3 className="text-xl font-bold text-white mb-3">Curated Experiences</h3>
                  <p className="text-gray-400 leading-relaxed">
                    From the moment you book to the final moment of your show, every detail is considered. 
                    We handle the setting, the ambiance, the performer match, and the follow-through. 
                    You tell us what you want — we make it happen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Ethics */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Safety & Ethics
          </h2>
          <p className="text-gray-400 mb-8 max-w-2xl">
            Premium entertainment requires premium standards. Here's how we protect everyone involved.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800/30">
              <h3 className="text-gold font-semibold mb-3 flex items-center gap-2">
                <span>🛡️</span> Performer Safety Protocols
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Every booking is vetted. Performers always have a safety contact, check-in protocols, 
                and the right to end any engagement at any time. No exceptions.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800/30">
              <h3 className="text-gold font-semibold mb-3 flex items-center gap-2">
                <span>🤝</span> Consent Frameworks
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Clear boundaries are communicated before every show. Both performers and clients 
                understand what's included and what isn't. Professionalism isn't optional — it's the baseline.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800/30">
              <h3 className="text-gold font-semibold mb-3 flex items-center gap-2">
                <span>🔒</span> Complete Discretion
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                No photos, no recording, no social media. Client privacy is absolute. 
                Performer identities are protected. What happens at your event stays there.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-gray-900/40 border border-gray-800/30">
              <h3 className="text-gold font-semibold mb-3 flex items-center gap-2">
                <span>⚖️</span> Fair Pay & Treatment
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Performers are paid fairly and promptly. No predatory commission structures. 
                We invest in our people because the quality of the experience depends on it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-b from-[#0a0a0f] via-purple-950/10 to-[#0a0a0f]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Experience the Difference
          </h2>
          <p className="text-gray-400 mb-6">
            Premium, private, persona-driven entertainment in Calgary.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book"
              className="px-10 py-4 bg-gold text-black font-bold text-lg rounded-full hover:bg-gold-light transition-all duration-300 shadow-lg shadow-gold/30"
            >
              Book Now
            </Link>
            <Link
              to="/entertainers"
              className="px-10 py-4 border border-purple-700 text-purple-400 font-bold text-lg rounded-full hover:bg-purple-700 hover:text-white transition-all duration-300"
            >
              Meet Our Entertainers
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
